import {
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    forwardRef,
} from '@angular/core';
import { ControlValueAccessor, FormControl, FormGroup, NG_VALUE_ACCESSOR } from '@angular/forms';

import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

import { ProjectService, ToastService, TranslationService } from '@mirage-projects/cw-commons';
import { EPSG, Geoid, ResultPage } from '@mirage-projects/cw-models';
import { UtilsPcvizService } from '@mirage-projects/cw-pcviz-core';

import { KeyValue } from '../../models/key-value';

@Component({
    selector: 'cw-crs-search',
    templateUrl: './crs-search.component.html',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => CrsSearchComponent),
            multi: true,
        },
    ],
    standalone: false,
})
export class CrsSearchComponent implements OnInit, OnDestroy, OnChanges, ControlValueAccessor {
    @Input() placeholder = '';
    @Input() selectItem: KeyValue | string = { key: '', value: '' };
    @Input() type: 'geoid' | 'crs' = 'crs';
    @Input() dataList: KeyValue[] = [];
    /** Displays the menu with position absolute over the rest of the content */
    @Input() floating = false;
    @Output() selectItemChange = new EventEmitter<KeyValue>();

    crsSearchForm = new FormGroup({
        selectedItem: new FormControl({
            key: '',
            value: '',
        } as KeyValue),
    });

    loading = false;
    currentPage = 1;
    searchText = '';
    totalResults = 0;
    selectedItemInfo = '';
    onChange: any;

    private onDestroy$: Subject<void> = new Subject();

    constructor(
        private translationService: TranslationService,
        private projectService: ProjectService,
        private toastService: ToastService,
        private changeDetectorRef: ChangeDetectorRef,
        private utilsService: UtilsPcvizService
    ) {}

    ngOnInit(): void {
        this.selectedValueChanges();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['selectItem']) {
            if (!this.selectItem) {
                this.selectItem = { key: '', value: '' };
            }

            if (typeof this.selectItem !== 'string') {
                this.setSelectItemValue(this.selectItem);
            } else {
                const searchText = this.selectItem.replace(/^EPSG:/, '');
                this.onSearchText(searchText, true);
            }
        }
    }

    ngOnDestroy(): void {
        this.onDestroy$.next();
    }

    onUpdateResults(id: number): void {
        this.currentPage = id;

        if (this.type === 'crs') {
            this.getEPSG(this.searchText);
        }
    }

    /**
     * Gets the search results when the search text is set
     * @param searchText The text to search to
     * @param shouldSelectMatchingItem If true, the matching item will be selected if the item key matches the search text, the search text property will be set later when the item matched is selected.
     */
    onSearchText(searchText: string, shouldSelectMatchingItem = false): void {
        const currentSelectedItem = this.crsSearchForm.getRawValue().selectedItem;
        if (currentSelectedItem?.value === searchText) {
            return;
        }

        this.selectedItemInfo = '';

        if (!shouldSelectMatchingItem) {
            this.searchText = searchText;
        }

        if (this.type === 'crs') {
            this.getEPSG(searchText, shouldSelectMatchingItem);
        } else {
            this.findGeoidsByID(searchText, shouldSelectMatchingItem);
        }
    }

    selectedValueChanges(): void {
        this.crsSearchForm
            .get('selectedItem')
            ?.valueChanges.pipe(takeUntil(this.onDestroy$))
            .subscribe((value: KeyValue | null) => {
                if (!value) {
                    this.selectedItemInfo = '';
                    this.selectItemChange.emit();
                } else {
                    if (value.key) {
                        this.getInfo(value.key);
                    }
                    this.selectItemChange.emit(value);
                }

                if (this.onChange) {
                    this.onChange(value);
                }
            });
    }

    /**
     * Gets the list of EPSG from a search text and current page
     */
    getEPSG(searchText: string, shouldSelectMatchingItem = false): void {
        this.loading = true;
        this.projectService
            .searchEPSG(searchText, this.currentPage)
            .pipe(take(1))
            .subscribe({
                next: (response: ResultPage<EPSG>) => {
                    this.totalResults = response.count;
                    const nonMappedDatalist = response.results;
                    this.dataList = nonMappedDatalist.map((result: EPSG) => {
                        return {
                            key: result.code,
                            value: result.name,
                        };
                    });
                    if (shouldSelectMatchingItem) {
                        this.setSelectedItemFromText(searchText);
                    }
                    this.loading = false;
                },
                error: () => {
                    this.loading = false;
                    this.toastService.error('CREATE_PROJECT.ERROR_CRS_SEARCH');
                },
            });
    }

    /**
     * Finds the geoids with id or description containing the search text
     * @param searchText Text to find
     */
    findGeoidsByID(searchText: string, shouldSelectMatchingItem = false): void {
        this.loading = true;

        this.utilsService
            .getCRSUtils()
            .findGeoidsByID(searchText)
            .then((dataList: Geoid[]) => {
                this.loading = false;

                this.dataList = dataList.map((geoidList: Geoid) => {
                    return { key: geoidList.geoidID, value: geoidList.description };
                });

                if (shouldSelectMatchingItem) {
                    this.setSelectedItemFromText(searchText);
                }

                this.changeDetectorRef.detectChanges();
            });
    }

    /**
     * Returns the selected geoid or ESPG extra information to show above the dropdown box
     * @param code Geiod or ESPG code to get the info
     */
    async getInfo(code: string): Promise<void> {
        const datum: string = await this.getDatumCode(code);

        const prefix = this.type === 'crs' ? 'EPSG:' : '';
        this.selectedItemInfo = `(${prefix} ${code} ${datum ? this.getDatumList(datum) : ''})`;
        this.changeDetectorRef.detectChanges();
    }

    getDatumCode(code: string): Promise<string> {
        return this.type === 'crs' ? this.getDatumCrs(code) : this.getDatumGeoid(code);
    }

    getDatumGeoid(code: string): Promise<string> {
        return this.utilsService.getCRSUtils().getGeoidDatums(code);
    }

    getDatumCrs(code: string): Promise<string> {
        return this.utilsService.getCRSUtils().getCRSDatum(code);
    }

    getDatumList(datum: string): string {
        return `${this.translationService.translate('PROJECTS.DATUM')} ${datum}`;
    }

    writeValue(value: KeyValue | string): void {
        this.selectItem = value;
    }

    registerOnChange(onChange: any): void {
        this.onChange = onChange;
    }

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    registerOnTouched(): void {}

    private setSelectedItemFromText(searchText: string): void {
        const selectItem = this.dataList.find((data) => data.key === searchText);
        if (selectItem) {
            this.selectItem = selectItem;
            this.selectedItemInfo = ' ';
            this.setSelectItemValue(selectItem, true);
        }
    }

    private setSelectItemValue(value: KeyValue, shoudSetSearchText = false): void {
        this.crsSearchForm.get('selectedItem')?.setValue(value);

        if (!value?.key) {
            this.selectedItemInfo = '';
            this.dataList = [];
            return;
        }

        if (shoudSetSearchText) {
            this.searchText = value.key;
        }

        this.dataList = [value];
    }
}
